import { CircularProgress, Checkbox } from "@mui/material";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { processErrorResponse } from "../../../helpers/processErrorResponse";
import useMobile from "../../../hooks/useOrientation";
import { addPassenger } from "../../../services/passengerService";
import { addUserWithPassenger } from "../../../services/userService";
import TermsAndConditions from "./TermsAndConditions";
import AddSignModal from "../../payment/AddSingModal";
import { SessionContext } from "../../../helpers/session";
import { addDoc, collection } from "firebase/firestore";
import { database } from "../../../config";
import { primary } from "../../../ui/colors";
import { getRegistrationMessage } from "../../../config/registrationMessages";

const primaryDark = "#00a896";
const primaryLight = "#e0faf7";

const RegistrationFinished = ({
  responsibleData,
  passengerData,
  tripData,
  onGoBack,
  isGrown,
  setUserRegisteredId,
  userRegisteredId,
  associatedUserId,
}) => {
  const { user } = useContext(SessionContext);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const isMobile = useMobile();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    ammount: null,
    imageUrl: null,
    fileType: null,
  });

  const handleSubmitForm = async () => {
    if (!termsAccepted) {
      setErrorMessage(
        "Debes aceptar los términos y condiciones para completar el registro.",
      );
      return;
    }
    setLoading(true);
    const userData = {
      name: responsibleData.name,
      lastName: responsibleData.lastName,
      email: responsibleData.email.toLowerCase(),
      password: "password",
      phone: responsibleData.phone,
      passenger: passengerData,
      groupId: tripData.group,
    };

    try {
      if (!user) {
        let userId;
        if (!userRegisteredId) {
          const userRes = await addUserWithPassenger(userData);
          setUserRegisteredId(userRes.id);
          userId = userRes.id;
        } else {
          userId = userRegisteredId;
        }
        if (associatedUserId) {
          await addDoc(collection(database, "companion"), {
            userId,
            principal: associatedUserId,
          });
        }
      } else {
        await addPassenger(
          { ...passengerData, userId: user.id },
          tripData.group,
        );
        if (associatedUserId) {
          await addDoc(collection(database, "companion"), {
            userId: user.id,
            principal: associatedUserId,
          });
        }
      }
      setFormSubmitted(true);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setErrorMessage(
        processErrorResponse(e)?.[1] ||
          "Error creando pasajero. Intentelo nuevamente.",
      );
    }
  };

  const handleSetPaymentData = (data) => {
    setPaymentData({
      fileType: data.file.type,
      imageUrl: data.file.file,
      ammount: data.ammount,
    });
    setShowSignModal(false);
  };

  if (formSubmitted) {
    return (
      <RegisterSuccess
        name={passengerData.name}
        trip={`${tripData.programName} - ${tripData.tripName}`}
        programId={tripData.program}
      />
    );
  }

  const sections = [
    ...(!isGrown
      ? [
          {
            title: "Padre/Madre/Tutor",
            icon: "👤",
            rows: [
              {
                label: "Nombre",
                value: `${responsibleData.name} ${responsibleData.lastName}`,
              },
              { label: "Email", value: responsibleData.email?.toLowerCase() },
              { label: "Teléfono", value: responsibleData.phone },
            ],
          },
        ]
      : []),
    {
      title: "Pasajero/a",
      icon: "🧳",
      rows: [
        {
          label: "Nombre",
          value: `${passengerData.name} ${passengerData.lastName}`,
        },
        {
          label:
            passengerData.documentType === "Pasaporte" ? "Pasaporte" : "Cédula",
          value: passengerData.documentId,
        },
        { label: "Fecha de nac.", value: passengerData.birthDate },
        { label: "Celular", value: passengerData.phone },
      ],
    },
    {
      title: "Viaje",
      icon: "✈️",
      rows: [
        { label: "Destino", value: tripData.programName },
        { label: "Programa", value: tripData.tripName },
        { label: "Grupo", value: tripData.groupName },
      ],
    },
  ];

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <h2 style={styles.title}>Verificá tus datos</h2>
        <p style={styles.subtitle}>
          Revisá que todo sea correcto antes de finalizar el registro.
        </p>
      </div>

      {/* Summary cards */}
      <div
        style={{
          ...styles.summaryGrid,
          gridTemplateColumns: isMobile
            ? "1fr"
            : `repeat(${sections.length}, 1fr)`,
        }}
      >
        {sections.map((section) => (
          <div key={section.title} style={styles.summaryCard}>
            <div style={styles.cardHeader}>
              <span style={styles.cardIcon}>{section.icon}</span>
              <span style={styles.cardTitle}>{section.title}</span>
            </div>
            {section.rows.map(({ label, value }) => (
              <div key={label} style={styles.dataRow}>
                <span style={styles.dataLabel}>{label}</span>
                <span style={styles.dataValue}>{value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Terms */}
      <div
        style={styles.termsRow}
        onClick={() => setTermsAccepted(!termsAccepted)}
      >
        <Checkbox
          checked={termsAccepted}
          style={{ color: primary, padding: 0 }}
          onChange={() => setTermsAccepted(!termsAccepted)}
          onClick={(e) => e.stopPropagation()}
        />
        <span style={styles.termsText}>
          Acepto los{" "}
          <span
            style={styles.termsLink}
            onClick={(e) => {
              e.stopPropagation();
              window.open("/terminosycondiciones");
            }}
          >
            Términos y condiciones del viaje
          </span>
        </span>
      </div>

      {/* Error */}
      {errorMessage && <div style={styles.errorBanner}>{errorMessage}</div>}

      {/* Actions */}
      <div style={styles.actionsRow}>
        <button
          style={styles.btnSecondary}
          onClick={onGoBack}
          disabled={loading}
        >
          ← Volver
        </button>
        <button
          style={{
            ...styles.btnPrimary,
            opacity: !termsAccepted || loading ? 0.5 : 1,
            cursor: !termsAccepted || loading ? "not-allowed" : "pointer",
          }}
          disabled={!termsAccepted || loading}
          onClick={handleSubmitForm}
        >
          {loading ? (
            <CircularProgress size={18} style={{ color: "#fff" }} />
          ) : (
            "Finalizar registro"
          )}
        </button>
      </div>

      {showSignModal && (
        <AddSignModal
          onCloseModal={() => setShowSignModal(false)}
          paymentData={paymentData}
          onSendPaymentData={handleSetPaymentData}
        />
      )}
    </div>
  );
};

export default RegistrationFinished;

// ---- Register Success ----

const RegisterSuccess = ({ name, trip, programId }) => {
  const { user } = useContext(SessionContext);
  const plane = require("../../../assets/plane.png");
  const navigate = useNavigate();
  const customMessage = getRegistrationMessage(programId);

  return (
    <div style={successStyles.container}>
      <div style={successStyles.iconWrapper}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="2.5"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <h1 style={successStyles.title}>¡Registro completado!</h1>
      {!customMessage && (
        <p style={successStyles.subtitle}>
          Felicitaciones <strong>{name}</strong>, tu viaje a{" "}
          <strong>{trip}</strong> está cada vez más cerca.
        </p>
      )}

      {!user ? (
        <>
          <img src={plane} alt="" style={successStyles.plane} />
          <div style={successStyles.infoCard}>
            <p style={successStyles.infoText}>
              {customMessage ||
                "Si llegaste hasta acá, estás por buen camino. Te vamos a acompañar durante el viaje y después seguiremos juntos. Porque College es más que un viaje."}
            </p>
            <p
              style={{ ...successStyles.infoText, color: "#888", marginTop: 8 }}
            >
              Cuando los datos sean validados, te enviaremos un mail con tu
              usuario y contraseña para ingresar al sistema.
            </p>
          </div>
          <button style={successStyles.btn} onClick={() => navigate("/login")}>
            Ir al login
          </button>
        </>
      ) : (
        <button
          style={successStyles.btn}
          onClick={() => (window.location.href = "./perfil")}
        >
          Ir al perfil
        </button>
      )}
    </div>
  );
};

// ---- Styles ----

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  header: {
    marginBottom: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 15,
    color: "#888",
  },
  summaryGrid: {
    display: "grid",
    gap: 12,
  },
  summaryCard: {
    border: "1.5px solid #e0e0e0",
    borderRadius: 12,
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: 10,
    borderBottom: "1px solid #f0f0f0",
    marginBottom: 2,
  },
  cardIcon: { fontSize: 16 },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
  },
  dataRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  dataLabel: {
    fontSize: 14,
    color: "#999",
    flexShrink: 0,
    minWidth: 60,
  },
  dataValue: {
    fontSize: 15,
    fontWeight: 500,
    color: "#111",
    textAlign: "right",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    minWidth: 0,
    flex: 1,
  },
  termsRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    cursor: "pointer",
    userSelect: "none",
  },
  termsText: {
    fontSize: 16,
    color: "#444",
  },
  termsLink: {
    color: primaryDark,
    textDecoration: "underline",
    cursor: "pointer",
  },
  errorBanner: {
    background: "#fdecea",
    border: "1px solid #f5c6c6",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#c0392b",
    fontSize: 13,
  },
  actionsRow: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
  },
  btnPrimary: {
    padding: "12px 28px",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Gilroy",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "opacity 0.15s",
  },
  btnSecondary: {
    padding: "12px 20px",
    background: "transparent",
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "#555",
    cursor: "pointer",
    fontFamily: "Gilroy",
  },
};

const successStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px 16px",
    textAlign: "center",
    gap: 16,
  },
  iconWrapper: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
  },
  subtitle: {
    fontSize: 15,
    color: "#555",
    maxWidth: 440,
    lineHeight: 1.6,
  },
  plane: {
    width: 80,
    marginTop: 8,
  },
  infoCard: {
    background: primaryLight,
    border: `1px solid ${primary}`,
    borderRadius: 12,
    padding: "16px 20px",
    maxWidth: 480,
    textAlign: "left",
  },
  infoText: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.6,
  },
  btn: {
    padding: "12px 32px",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Gilroy",
    marginTop: 8,
  },
};
